import React from "react";
import {
  Typography,
  TextField,
  Grid,
  InputLabel,
  FormControlLabel,
  Checkbox,
  Autocomplete,
  Button,
  CircularProgress,
  Stack,
} from "@mui/material";
import DatePicker from "@mui/lab/DatePicker";
import AdapterDateFns from "@mui/lab/AdapterDateFns";
import LocalizationProvider from "@mui/lab/LocalizationProvider";

import { makeStyles } from "@mui/styles";

const useStyles = makeStyles((theme) => ({
  paper: {
    marginTop: theme.spacing(5),
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  button: {
    marginLeft: theme.spacing(1),
  },
}));

export const RangeDates = (props) => {
  const {
    currentPricing,
    dateRange,
    orders,
    ordersSelected,
    priceMargin,
    receipts,
    receiptsSelected,
    pricingType,
    showPricingDifferences,
    handleChangeCurrentPricing,
    handleChangeOrders,
    handleChangeRangeDate,
    handleChangeReceipts,
    handleChangePriceMargin,
    handleChangePricingType,
    handleChangeShowPricingDifferences,
  } = props;
  const classes = useStyles();

  return (
    <div className={classes.paper}>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={12}>
          <Typography component="h4" style={{ textAlign: "center" }}>
            Load orders from a given date and select the orders to include.
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              disableToolbar
              variant="inline"
              format="MM/dd/yyyy"
              margin="normal"
              KeyboardButtonProps={{
                "aria-label": "change date",
              }}
              value={dateRange[0]}
              renderInput={(props) => (
                <TextField
                  {...props}
                  fullWidth
                  label="Select start date"
                  variant="standard"
                />
              )}
              onChange={(date) =>
                handleChangeRangeDate({
                  target: {
                    name: "start_date",
                    value: date,
                  },
                })
              }
            />
          </LocalizationProvider>
        </Grid>
        <Grid item xs={12} sm={6}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              disableToolbar
              variant="inline"
              format="MM/dd/yyyy"
              margin="normal"
              KeyboardButtonProps={{
                "aria-label": "change date",
              }}
              value={dateRange[1]}
              renderInput={(props) => (
                <TextField
                  {...props}
                  fullWidth
                  label="Select end date"
                  variant="standard"
                />
              )}
              onChange={(date) =>
                handleChangeRangeDate({
                  target: {
                    name: "end_date",
                    value: date,
                  },
                })
              }
            />
          </LocalizationProvider>
        </Grid>
        {dateRange[0] !== null && dateRange[1] !== null ? (
          <Grid item xs={12} sm={12}>
            <InputLabel shrink id="orders">
              Orders
            </InputLabel>
            <Autocomplete
              fullWidth
              multiple
              disablePortal
              size="small"
              name="orders"
              options={orders}
              getOptionLabel={(option) =>
                `${option.customer ? option.customer.username : "Anonymous"} #${
                  option.id
                }`
              }
              renderInput={(params) => (
                <TextField {...params} size="small" variant="standard" />
              )}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              onChange={(e, value) => handleChangeOrders(e, value)}
              helpertext="Select the orders to include in the receipt difference."
              value={ordersSelected}
            />
          </Grid>
        ) : (
          <></>
        )}
        <Grid item xs={12} sm={12}>
          <InputLabel shrink id="stripeLabel">
            Pricing type
          </InputLabel>

          <Stack direction="row" spacing={1}>
            <Button
              style={{ marginBottom: 10 }}
              variant="contained"
              color={pricingType === "shelf" ? "primary" : "inherit"}
              onClick={handleChangePricingType("shelf")}
              className={classes.button}
            >
              Shelf
            </Button>
            <Button
              style={{ marginBottom: 10 }}
              variant="contained"
              color={pricingType === "contract" ? "primary" : "inherit"}
              onClick={handleChangePricingType("contract")}
              className={classes.button}
            >
              Contract
            </Button>
          </Stack>
        </Grid>
        {receipts.length ? (
          <Grid item xs={12} sm={12}>
            <InputLabel shrink id="stripeLabel">
              Receipts
            </InputLabel>
            <Autocomplete
              fullWidth
              multiple
              disablePortal
              size="small"
              name="receipts"
              options={receipts}
              getOptionLabel={(option) =>
                `${option.description} (${option.id})${
                  option.user_uploaded ? ` - user uploaded` : ``
                }`
              }
              renderInput={(params) => (
                <TextField {...params} size="small" variant="standard" />
              )}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              onChange={(e, value) => handleChangeReceipts(e, value)}
              value={receiptsSelected}
            />
          </Grid>
        ) : (
          <Grid item xs={12} sm={12}>
            <InputLabel shrink id="stripeLabel">
              Receipts
            </InputLabel>
            <CircularProgress />
          </Grid>
        )}
        <Grid item xs={12} sm={12}>
          <Stack direction="row" spacing={1}>
            <div>
              <InputLabel shrink id="stripeLabel">
                Price Margin
              </InputLabel>
              <TextField
                disabled={!showPricingDifferences}
                InputProps={{
                  inputProps: { min: 0 },
                }}
                size="small"
                variant="standard"
                name="price_margin"
                value={priceMargin}
                type="number"
                onChange={handleChangePriceMargin}
              />
            </div>
            <FormControlLabel
              value="start"
              control={
                <Checkbox
                  disabled={!showPricingDifferences}
                  name="current_pricing"
                  value={currentPricing}
                  checked={currentPricing}
                  onChange={handleChangeCurrentPricing}
                  color="primary"
                />
              }
              label="Use Current Pricing"
              labelPlacement="end"
            />
            <FormControlLabel
              value="start"
              control={
                <Checkbox
                  disabled={pricingType === "contract"}
                  name="show_pricing_differences"
                  value={showPricingDifferences}
                  checked={showPricingDifferences}
                  onChange={handleChangeShowPricingDifferences}
                  color="primary"
                />
              }
              label="Show Pricing Differences"
              labelPlacement="end"
            />
          </Stack>
        </Grid>
      </Grid>
    </div>
  );
};

export default RangeDates;
